/**
 * QuickAppointmentModal — popup "Nuovo appuntamento" usabile inline da Pipeline
 * e scheda contatto. Riusa il modale moderno ApptModal (operatore per singolo
 * servizio, buffer di pulizia, riordino servizi, calcolo fine automatico).
 * Tenant-scoped via useApiClient(): nel workspace Agenzia opera sul salone
 * selezionato, nell'app salone sul tenant corrente.
 */
import { useEffect, useState } from "react";
import { useApiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { ApptModal } from "@/pages/salon/agenda/ApptModal";
import { ymd, isoAt, addDays, slotInRange, DEFAULT_OPEN_HOURS } from "@/pages/salon/agenda/utils";

function emptyForm(d, clientId = "") {
  return {
    client_id: clientId,
    service_items: [],
    staff_id: "",
    start_date: d,
    start_time: "10:00",
    end_time: "10:30",
    notes: "",
    status: "scheduled",
  };
}

export default function QuickAppointmentModal({ clientId = "", onClose, onSaved }) {
  const api = useApiClient();
  const today = ymd(new Date());
  const [form, setForm] = useState(() => emptyForm(today, clientId));
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const from = today, to = addDays(today, 90);
        const [c, sv, st, t, ov] = await Promise.all([
          api.get("/clients?limit=5000"),
          api.get("/services"),
          api.get("/staff"),
          api.get("/tenant").catch(() => ({ data: null })),
          api.get(`/hours-overrides?from=${from}&to=${to}`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setClients(c.data.items || c.data || []);
        setServices(sv.data || []);
        setStaff(st.data || []);
        setTenant(t.data);
        setOverrides(ov.data || []);
      } catch {
        if (alive) toast.error("Errore caricamento dati");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  const openHours = tenant?.opening_hours || DEFAULT_OPEN_HOURS;

  const dayCfgFor = (dateStr, staffId = null) => {
    const staffOv = staffId ? overrides.find((o) => o.date === dateStr && o.staff_id === staffId) : null;
    const salonOv = overrides.find((o) => o.date === dateStr && !o.staff_id);
    if (staffOv) {
      return staffOv.kind === "closed"
        ? { closed: true, _override: true }
        : { closed: false, open: staffOv.open, close: staffOv.close, open2: staffOv.open2, close2: staffOv.close2, _override: true };
    }
    if (salonOv) {
      return salonOv.kind === "closed"
        ? { closed: true, _override: true }
        : { closed: false, open: salonOv.open, close: salonOv.close, open2: salonOv.open2, close2: salonOv.close2, _override: true };
    }
    const dow = new Date(dateStr + "T00:00:00Z").getUTCDay();
    const key = String(dow === 0 ? 6 : dow - 1);
    return openHours[key] || { closed: true };
  };

  const isAvailable = (targetDate, hour, minute, staffId) => {
    const cfg = dayCfgFor(targetDate, staffId);
    if (cfg.closed) return false;
    const t = hour * 60 + minute;
    const p = (hhmm) => { const [h, m] = (hhmm || "").split(":").map(Number); return h * 60 + m; };
    const morning = cfg.open && cfg.close && p(cfg.open) <= t && t < p(cfg.close);
    const afternoon = cfg.open2 && cfg.close2 && p(cfg.open2) <= t && t < p(cfg.close2);
    return morning || afternoon;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Seleziona il cliente"); return; }
    if (!form.service_items.length) { toast.error("Aggiungi almeno un servizio"); return; }
    const [sh, sm] = form.start_time.split(":").map(Number);
    const [eh, em] = form.end_time.split(":").map(Number);
    const start = isoAt(form.start_date, sh, sm);
    const end = isoAt(form.start_date, eh, em);
    if (new Date(end) <= new Date(start)) { toast.error("Fine deve essere dopo l'inizio"); return; }
    const st = staff.find((s) => s.id === form.staff_id);
    const inHoursSalon = slotInRange(form.start_date, sh, sm, openHours);
    const inHoursStaff = !st?.working_hours || slotInRange(form.start_date, sh, sm, st.working_hours);
    if (!inHoursSalon || !inHoursStaff) {
      const msg = !inHoursSalon
        ? "Questo appuntamento è fuori dagli orari di apertura del salone. Confermi comunque?"
        : "Il collaboratore selezionato non risulta in servizio in questo orario. Confermi comunque?";
      if (!window.confirm(msg)) return;
    }
    const service_ids = form.service_items.map((it) => it.service_id);
    const primary_staff = form.staff_id || form.service_items.find((it) => it.staff_id)?.staff_id || "";
    try {
      await api.post("/appointments", {
        client_id: form.client_id,
        service_ids,
        service_items: form.service_items.map((it) => ({
          service_id: it.service_id,
          staff_id: it.staff_id || null,
          buffer_after_min: Number(it.buffer_after_min || 0),
        })),
        staff_id: primary_staff,
        start,
        end,
        notes: form.notes || null,
        status: form.status,
      });
      toast.success("Appuntamento creato");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center" data-testid="quick-appt-modal" onClick={onClose}>
        <div className="sp-card px-6 py-4 text-sm text-[color:var(--sp-text-muted)]" onClick={(e) => e.stopPropagation()}>Caricamento…</div>
      </div>
    );
  }

  return (
    <div data-testid="quick-appt-modal">
      <ApptModal
        onClose={onClose}
        onSubmit={submit}
        form={form}
        setForm={setForm}
        clients={clients}
        services={services}
        staff={staff}
        editing={null}
        openHours={openHours}
        dayCfgFor={dayCfgFor}
        isAvailable={isAvailable}
      />
    </div>
  );
}
